import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ClientCertificate, ENVIRONMENT, Environment, FrontdoorToken } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { map, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';
import { URLS } from './urls';


/** A page of certificate request tokens read from the v3 API: the rows, and the total the service gave. */
export interface FrontdoorTokenPageV3 {
    tokens: FrontdoorToken[];
    total: number | null;
}

/**
 * A new certificate request token.
 *
 * `name` becomes the name of the certificate the token produces, and is unique within the account
 * across both tokens and certificates. Every subject field is a constraint the signing request must
 * then satisfy, so each one left out is one the holder is free to choose.
 *
 * `clientCertificateId` makes it a renewal token: redeeming it replaces that certificate in place
 * rather than minting a new one. The name must then be the certificate's, and the service fills the
 * subject and contact from the certificate where they are left out. Issuing one also needs update
 * permission on the certificate, and it revokes any earlier renewal token for the same certificate.
 */
export interface CreateFrontdoorTokenV3 {
    name: string;
    commonName?: string;
    organization?: string;
    organizationalUnit?: string;
    validYears?: number;
    contactEmail?: string;
    clientCertificateId?: string;
}

/**
 * Changes to a certificate request token already on file.
 *
 * The clearable fields are `string | null` rather than optional: the service reads an absent field
 * as "leave it alone" and a null one as "clear it", so an emptied box has to travel as null or the
 * constraint it held would silently survive the edit.
 */
export interface UpdateFrontdoorTokenV3 {
    name: string;
    commonName: string | null;
    organization: string | null;
    organizationalUnit: string | null;
    contactEmail: string | null;
    validYears?: number;
}

/**
 * The signed enrollment artifact a certificate request token exchanges for. One JWT in place of
 * the old three-fact hand-off of API base url, frontdoorId and bare token.
 *
 * `url` is the enroll endpoint the server says this JWT is valid against. Trustworthy here because
 * it arrived on an authenticated read — the same field decoded out of the JWT on the public upload
 * page is not, because nothing checks the signature there.
 */
export interface EnrollmentJwtV3 {
    frontdoorId: string;
    certificateRequestTokenId: string;
    jwt: string;
    url: string;
    expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class FrontdoorTokenService extends HateoasResourceOperation<FrontdoorToken> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    model: ClientCertificate | null = null;
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(FrontdoorToken);
        this.apiUrl = this.environment.frontdoorURL;

    }

    public findTokens(id: string, isV2: boolean, options?: PagedGetOption): Promise<FrontdoorToken[]> {
          const headers = new HttpHeaders().set('Accept', 'application/hal+json');
          const version = isV2 ? '/v2' : '';
        
          let params = new HttpParams().set('sort', 'createdAt,DESC');
        if(options) {
    if (options.pageParams) {
      Object.keys(options.pageParams).forEach(key => {
        const value = options.pageParams[key]; // <-- Corrected
        if (value !== undefined && value !== null) {
          params = params.append(key, String(value));
        }
      });
    }

   // Handle the nested params object
    if (options.params) {
      Object.keys(options.params).forEach(key => {
        const value = options.params[key]; // <-- Corrected
        if (value !== undefined && value !== null) {
          params = params.append(key, String(value));
        }
      });
    
  }
}
        return this.http
                .get<any>(`${this.apiUrl}${version}/${id}/certificate-request-tokens`, {
                    headers,
                    params,
                    })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.page.totalPages;
                    this.lastTotalCount = val.page.totalElements;
                })
            )
            .toPromise()
    }

    /**
     * The certificate request tokens of a v3 frontdoor. Its own method rather than another value
     * of the `isV2` flag above: that flag is a boolean at every existing call site, and a third
     * version does not fit in it.
     *
     * The `/v3` segment is the gateway's, not the service's. The gateway strips `frontdoor/v3` and
     * the frontdoor service maps every child domain at `/{frontdoorId}/<plural>`.
     */
    public findV3Tokens(frontdoorId: string, size = 2000): Observable<FrontdoorTokenPageV3> {
        const params = new HttpParams().set('sort', 'createdAt,DESC').set('size', String(size));
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens`, {
                headers: this.halHeaders(),
                params,
            })
            .pipe(map((response: any) => this.readV3Page(response)));
    }

    /**
     * Write a certificate request token into a v3 frontdoor.
     *
     * The token's own expiry is the service's to set, so nothing here offers it: the link this
     * token cuts is meant to be spent soon after it is handed over.
     */
    public createV3(frontdoorId: string, create: CreateFrontdoorTokenV3): Observable<FrontdoorToken> {
        return this.http.post<FrontdoorToken>(
            `${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens`,
            create,
            { headers: this.halHeaders() }
        );
    }

    /** One certificate request token of a v3 frontdoor, for the form that edits it. */
    public findV3Token(frontdoorId: string, id: string): Observable<FrontdoorToken> {
        return this.http.get<FrontdoorToken>(
            `${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens/${id}`,
            { headers: this.halHeaders() }
        );
    }

    /**
     * Change a certificate request token already on file.
     *
     * PATCH and not PUT, so a field this does not name keeps what it had rather than being
     * cleared by its absence.
     */
    public updateV3(frontdoorId: string, id: string, update: UpdateFrontdoorTokenV3): Observable<FrontdoorToken> {
        return this.http.patch<FrontdoorToken>(
            `${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens/${id}`,
            update,
            { headers: this.halHeaders() }
        );
    }

    /**
     * The signed enrollment JWT for one token of a v3 frontdoor.
     *
     * Read one row at a time, on demand. Reading it for a whole listing would put a request per row
     * in front of the first paint, and would mint a live credential for every row whether or not
     * anybody shares it.
     */
    public findV3EnrollmentJwt(frontdoorId: string, id: string): Observable<EnrollmentJwtV3> {
        return this.http.get<EnrollmentJwtV3>(
            `${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens/${id}/enrollment-jwt`,
            { headers: this.halHeaders() }
        );
    }

    /**
     * The customer-facing link for a v3 enrollment JWT. The one place that builds it.
     *
     * The JWT rides in the url FRAGMENT and never in a query parameter. A fragment is not sent to
     * a server, so the credential stays out of `Referer` headers, server access logs and proxy
     * logs, which a `?jwt=` would land in.
     */
    public buildV3ShareUrl(jwt: string): string {
        const originDomain = window?.location?.hostname || this.environment.originDomain;
        const originPort = window?.location?.port || this.environment.originPort;
        const originProtocol = window?.location?.protocol || 'https:';
        let uiLink = originProtocol + '//' + originDomain;
        if (originPort) {
            uiLink = uiLink + ':' + originPort;
        }
        const fragment = jwt ? `#jwt=${encodeURIComponent(jwt)}` : '';
        return uiLink + URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V3 + fragment;
    }

    /** Remove one token from a v3 frontdoor. */
    public deleteV3(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/v3/${frontdoorId}/certificate-request-tokens/${id}`, {
            headers: this.halHeaders(),
        });
    }

    public delete(frontdoorId: string, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/certificate-request-tokens/${id}`;
            return this.http
                .delete(fullpath, {headers: headers})
    }


    public getTokens(frontdoorId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/certificate-request-tokens`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getCSRById(frontdoorId: string, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/certificate-request-tokens/${id}`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public create(model, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${id}/certificate-request-tokens`;
            return this.http
                .post(fullpath, model)
    }

        public update(model, frontdoorId: string, id, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/certificate-request-tokens/${id}`;
            return this.http
                .patch(fullpath, model)
    }



    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    private halHeaders(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readV3Page(response: any): FrontdoorTokenPageV3 {
        if (Array.isArray(response)) {
            return { tokens: response, total: response.length };
        }
        const embedded = response?._embedded;
        const collection = embedded ? embedded.certificateRequestTokens ?? Object.values(embedded)[0] : undefined;
        const tokens = Array.isArray(collection)
            ? collection
            : Array.isArray(response?.content)
              ? response.content
              : [];
        if (!Array.isArray(collection) && !Array.isArray(response?.content)) {
            this.logger.error('findV3Tokens: unrecognised response shape', response);
        }
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { tokens, total };
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

                  return headers.append('Content-Type', 'application/json');
    }

        /**
     * Generic error handler for bad requests
     */
        protected handleError(scope: any, error: HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred. Handle it accordingly.
                scope.logger.error('Feature Service error occurred:', error.error);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
            }
            scope.lastErrorSource.next(error);
            return observableThrowError(error);
        }
}
